import { useEffect, useRef, useState } from 'react';
import {
  ContainerMissionCard,
  ContainerStatus,
  ContainerStatusInfo,
  SocialMediaIcon,
  VerticalDivider,
  RightContent,

} from './style-component';
import { MissionStatusProps } from './mission-status-types';
import { TimeInfo } from '../time-info/time-info';
import { useNavigate } from 'react-router-dom';
import { getMissionTypeFromObjectives } from '@/utils/get-social-media-type/get-social-media-type';
import MissionRequirements from './components/mission-requirements/mission-requirements';
import { useTheme } from '@/contexts/theme-context/useThemeContext';
import { useMission } from '@/hook/useMission/useMission';
import useCampaignMission from '@/hook/useCampaignMission/useCampaignMission';
import { validateIfUserClaimedBenefit } from '@/services/benefits/benefit';
import { Objective } from '@/types/objective/objective-type';
import useFetch from '@/hook/useFetch/useFetch';
import { useUserContext } from '@/contexts/user/useUserContext';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import SkeletonLoader from '../skeleton-loader/skeleton-loader';
import { useSupportedSocialMediaContext } from '@/contexts/supported-social-media-context/useSupportedSocialMediaContext';
import { getInfoObjectiveRank } from '@/services/mission/missions';
import { ParticipantTableType } from '../table-contest/table-contest-type';
import { calculateMissionStatus } from '@/utils/calculate-mission-status/calculate-mission-status';
import MissionDetails from '../mission-card/components/mission-card-details/mission-card-details';
import { ALLOWED_OBJECTIVE_REQUIREMENTS } from '../missions-render-v2/components/requirements-objective/utils';

const MissionStatus: React.FC<MissionStatusProps> = ({ mission, onFunction }) => {
  const navigate = useNavigate();
  const { isConnect, user } = useUserContext();
  const theme = useTheme();
  const isDark = theme.theme.name === 'dark';
  const haveObjectiveExchange = mission?.objectives?.some((objective: Objective) => objective.type?.includes('EXCHANGE'));
  const { loadingCampaignOrParticipant, loadingMissions, requirements, participant } = useCampaignMission(mission.campaign_id, mission.id);
  const idParticipant = participant?.[0]?.id;
  const params = useRef({ participant_id: idParticipant, benefit_of_mission_mission_id: mission?.id });
  const { data: claim, loading: loadingClaimed, startFetch: validateClaim } = useFetch(validateIfUserClaimedBenefit, params.current, { autoFetch: false });
  const isClaimed = Array.isArray(claim) && claim.length > 0;
  const { missionState, isCompleted, loadingProgress } = useMission(mission, [], { filterRequirements: true });
  const { socialMedia } = useSupportedSocialMediaContext();
  const { type: missionType, icon: sourceIcon } = getMissionTypeFromObjectives(
    missionState?.objectives ?? [], 
    socialMedia
  );

  const progress = missionState?.objectives?.filter(objective => {
    const currentProgress = objective.participant_progress?.current_progress ?? 0;
    return currentProgress >= objective.quantity;
  }).length ?? 0;

  const firstRender = useRef<boolean>(true);
  useEffect(()=>{
    if (!loadingClaimed && isConnect && idParticipant && firstRender.current) {
      firstRender.current = false;
      params.current.participant_id = idParticipant;
      validateClaim();
    }
  }, [loadingClaimed, isConnect, validateClaim, idParticipant]);

  useEffect(() => {
    onFunction?.(missionState,isClaimed);
  }, [isClaimed, missionState]); //eslint-disable-line

  const paramsRank = {
      ...(user?.main_key && { user_main_key: user?.main_key }),
      limit: 1,
      page: 0,
      order: 'id'
  };

  const [participantRank, setParticipantRank] = useState<ParticipantTableType[]>([]);

  useEffect(() => {
      if (missionType === "Contest") {
          getInfoObjectiveRank(paramsRank)
              .then((data) => setParticipantRank(data))
              .catch((err) => console.error(err))
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionType, user?.main_key]); 

  const status = calculateMissionStatus(missionState, isCompleted, isClaimed, participantRank[0]?.current_position === 1);
  const filteredObjectives = missionState?.objectives?.filter((objective: Objective) => {
    const newType = objective?.type?.split(':')?.[0];
    return !ALLOWED_OBJECTIVE_REQUIREMENTS.includes(newType);
  }) ?? [];

  return (
    <ContainerMissionCard onClick={() => navigate(`/mission/${missionState?.campaign_id}/${missionState?.id}`)}>
      <MissionDetails 
        maxProgress={filteredObjectives?.length ?? 0}
        progress={progress}
        status={status}
        isClaimed={isClaimed}
        isExChange={haveObjectiveExchange}
        title={missionState?.name ?? `Misión ${missionState?.id}`}
        src={missionState?.benefits?.[0]?.reward?.thumb_url}
        missionType={missionType}
        participantRank={participantRank}
        loadingRank={loadingCampaignOrParticipant}
        rankNumber={participantRank[0]?.current_position?.toString() ?? ''}
      />
      <ContainerStatusInfo>
        <ContainerStatus>
          {loadingCampaignOrParticipant || loadingMissions || loadingClaimed || loadingProgress  ?
          <SkeletonLoader data-testid="mission-progress-skeleton" paragraph={false} title={true} /> :
          <TimeInfo
            endDate={missionState?.end_date}
            startDate={missionState?.start_date}
            isCampaign={false}
            status={status}
          />}
          <RightContent>
            {!isClaimed && (
              <>
                {loadingMissions  ?
                <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} size="small" /> :
                <MissionRequirements
                  requirements={requirements}
                />}
                <VerticalDivider />
              </>
            )}
            {sourceIcon && (
              <SocialMediaIcon src={sourceIcon} $isDark={isDark} />
            )}
          </RightContent>
        </ContainerStatus>
      </ContainerStatusInfo>
    </ContainerMissionCard>
  );
};

export default MissionStatus;
